import { useState } from 'react';
import { IconButton, Tab, Tabs, Tooltip } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import PageHeader from '../components/PageHeader';
import TabHeader from '../components/TabHeader';
import ConfirmDeleteDialog from '../components/ConfirmDeleteDialog';
import type { SettingsSupplierRow } from '../features/settings/types';
import SupplierRow from '../features/settings/components/suppliers/SupplierRow';
import SupplierModal from '../features/settings/components/suppliers/SupplierModal';
import { useManageSuppliers } from '../features/settings/hooks/useManageSuppliers';
import { useNotification } from '../context/useNotification';
import { SUPPLIERS_TAB_LABELS as LABELS } from '../utils/labels';
import { hasFullAccess } from '../utils/roles';
import type { MemberRole } from '../types/roles';

interface SuppliersPageProps {
  orgId: string | null;
  isSuperAdmin: boolean;
  role: MemberRole | null;
}

interface SupplierModalState {
  mode: 'create' | 'edit';
  supplier?: SettingsSupplierRow;
}

export default function SuppliersPage({ orgId, isSuperAdmin, role }: SuppliersPageProps) {
  const canEdit = hasFullAccess(role, isSuperAdmin);
  const {
    suppliers,
    archivedSuppliers,
    loadArchived,
    addSupplier,
    updateSupplier,
    archiveSupplier,
    unarchiveSupplier,
    deleteSupplier,
  } = useManageSuppliers(orgId);
  const { notifySuccess, notifyError } = useNotification();

  const [tab, setTab] = useState<'active' | 'archived'>('active');
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [supplierModal, setSupplierModal] = useState<SupplierModalState | null>(null);
  const [pendingDeleteId, setPendingDeleteId] = useState<string | null>(null);

  const handleTabChange = (_: React.SyntheticEvent, value: 'active' | 'archived') => {
    setTab(value);
    if (value === 'archived') loadArchived();
  };

  const handleConfirmDelete = async () => {
    if (!pendingDeleteId) return;
    const id = pendingDeleteId;
    setPendingDeleteId(null);
    setDeleteError(null);
    const result = await deleteSupplier(id);
    if (result === 'ok') {
      notifySuccess(LABELS.deleteSuccess);
    } else if (result === 'has_machines') {
      setDeleteError(LABELS.deleteErrorHasMachines);
      notifyError(LABELS.deleteErrorHasMachines);
    } else {
      setDeleteError(LABELS.deleteError);
      notifyError(LABELS.deleteError);
    }
  };

  const handleSave = async (name: string, email: string): Promise<boolean> => {
    const ok = supplierModal?.mode === 'create'
      ? await addSupplier(name, email)
      : supplierModal?.supplier
        ? await updateSupplier(supplierModal.supplier.id, name, email)
        : false;
    if (ok) notifySuccess(LABELS.saveSuccess); else notifyError(LABELS.mutationError);
    return ok;
  };

  const handleArchive = async (id: string) => {
    const ok = await archiveSupplier(id);
    if (ok) notifySuccess(LABELS.archiveSuccess); else notifyError(LABELS.mutationError);
  };

  const handleUnarchive = async (id: string) => {
    const ok = await unarchiveSupplier(id);
    if (ok) notifySuccess(LABELS.unarchiveSuccess); else notifyError(LABELS.mutationError);
  };

  const list = tab === 'active' ? suppliers : archivedSuppliers;

  return (
    <div className="h-dvh flex flex-col bg-background">
      <PageHeader />
      <main className="flex-1 overflow-y-auto p-4 md:p-8">
        <div className="max-w-xl mx-auto space-y-6">
          <TabHeader
            title={LABELS.title}
            description={LABELS.subtitle}
            addLabel={canEdit ? LABELS.newButton : undefined}
            onAdd={canEdit ? () => setSupplierModal({ mode: 'create' }) : undefined}
          />

          {deleteError && (
            <div className="rounded-xl bg-red-50 border border-red-200 px-4 py-2 flex items-center gap-2">
              <p className="text-sm text-red-700 flex-1">{deleteError}</p>
              <Tooltip title={LABELS.dismissError}>
                <IconButton
                  size="small"
                  onClick={() => setDeleteError(null)}
                  aria-label={LABELS.dismissError}
                >
                  <CloseIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </div>
          )}

          <div className="rounded-2xl border border-border overflow-hidden bg-surface">
            <Tabs value={tab} onChange={handleTabChange} className="border-b border-border px-2">
              <Tab label={LABELS.tabActive} value="active" />
              <Tab label={LABELS.tabArchived} value="archived" />
            </Tabs>

            {list.length === 0 ? (
              <p className="text-sm text-text-secondary text-center py-6">
                {tab === 'active' ? LABELS.noActive : LABELS.noArchived}
              </p>
            ) : (
              list.map((s, i) => (
                <SupplierRow
                  key={s.id}
                  supplier={s}
                  showDivider={i > 0}
                  onEdit={canEdit ? () => setSupplierModal({ mode: 'edit', supplier: s }) : undefined}
                  onArchive={canEdit && !s.archived ? () => handleArchive(s.id) : undefined}
                  onUnarchive={canEdit && s.archived ? () => handleUnarchive(s.id) : undefined}
                  onDelete={canEdit ? () => setPendingDeleteId(s.id) : undefined}
                />
              ))
            )}
          </div>

          {supplierModal && (
            <SupplierModal
              supplier={supplierModal.supplier}
              onClose={() => setSupplierModal(null)}
              onSave={handleSave}
            />
          )}

          <ConfirmDeleteDialog
            open={!!pendingDeleteId}
            onClose={() => setPendingDeleteId(null)}
            onConfirm={handleConfirmDelete}
          />
        </div>
      </main>
    </div>
  );
}
